import { useState } from "react";
import Header from "@/atomic/atoms/Header";
import OrgPayment from "@/atomic/organisms/orgPayment";
import { PropsView } from "./Models";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";

type PaymentMethod = "card" | "pix" | "savedCard";

const SUPPORTED_BRANDS = ["visa", "mastercard", "amex", "elo"];

export default function RechargeView({
  goBack,
  goToCard,
  goToPix,
  goToNewCard,
  plate,
  total,
  passagesTotal,
  finesTotal,
  savedCard,
}: PropsView) {
  const { t } = useI18n();
  const { themeName } = useThemeContext();
  const title = themeName === "zapay" ? "Pedágio" : t("pages:checkout:payment");

  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
    savedCard ? "savedCard" : null,
  );

  const formatCurrency = (value: number) =>
    value ? `R$ ${value.toFixed(2).replace(".", ",")}` : "R$ 0,00";

  const formattedTotal = formatCurrency(total);
  const formattedPassages = formatCurrency(passagesTotal);
  const formattedFines = formatCurrency(finesTotal);
  const formattedPlate = plate?.toUpperCase() ?? "";

  const handleConfirm = () => {
    if (selectedMethod === "card") goToNewCard();
    if (selectedMethod === "pix") goToPix();
    if (selectedMethod === "savedCard") goToCard();
  };

  const lastFour = savedCard?.number?.slice(-4) ?? "";
  const brandName = savedCard?.brand ?? "";
  const hasBrandIcon = SUPPORTED_BRANDS.includes(brandName);

  return (
    <div className="h-full flex flex-col">
      <Header title={title} goBack={goBack} hasLeftButton />

      <main className="flex-1 overflow-auto px-4 pb-28 bg-white">
        <div className="max-w-[480px] mx-auto">
          <h2 className="text-[22px] font-bold text-text mt-6 tracking-[-0.2px] leading-[1.2]">
            {t("pages:checkout:paymentMethod")}
          </h2>
          <p className="text-sm text-text-secondary mt-2 leading-[1.3]">
            {t("pages:checkout:paymentMethodDescription")}
          </p>

          <div className="mt-5 bg-background border border-border rounded-2xl p-5">
            <div className="flex justify-between items-center">
              <p className="text-base font-medium text-text tracking-[-0.4px]">
                {t("pages:checkout:payment")}
              </p>
              <p className="text-sm text-text-secondary tracking-[-0.4px]">
                {t("common:plate")} : {formattedPlate}
              </p>
            </div>

            <div className="w-full h-px bg-border my-4" />

            <div className="flex flex-col gap-3.5">
              <div className="flex justify-between items-center">
                <p className="text-sm text-text-secondary tracking-[-0.4px]">
                  {t("pages:checkout:passages")}
                </p>
                <p className="text-sm font-medium text-text tracking-[-0.4px]">
                  {formattedPassages}
                </p>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm text-text-secondary tracking-[-0.4px]">
                  {t("pages:checkout:fines")}
                </p>
                <p className="text-sm font-medium text-text tracking-[-0.4px]">
                  {formattedFines}
                </p>
              </div>
            </div>

            <div className="w-full h-px bg-border my-4" />

            <div className="flex justify-between items-center">
              <p className="text-base font-medium text-text tracking-[-0.4px]">
                {t("pages:checkout:total")}
              </p>
              <p className="text-base font-medium text-text tracking-[-0.4px]">
                {formattedTotal}
              </p>
            </div>
          </div>

          <div className="mt-4 bg-white border border-border rounded-2xl p-5">
            <div className="flex justify-between items-center">
              <p className="text-base font-medium text-text tracking-[-0.4px]">
                {t("pages:checkout:paymentMethods")}
              </p>
              <img
                src="/svg/icon-arrow-up-2.0.svg"
                width={20}
                height={20}
                alt=""
                className="opacity-60"
              />
            </div>

            <div className="mt-6 flex flex-col gap-4">
              <button
                type="button"
                onClick={() => setSelectedMethod("card")}
                className="flex items-center justify-between w-full text-start"
              >
                <div className="flex items-center gap-3">
                  <img
                    src="/svg/icon-card-2.0.svg"
                    width={24}
                    height={24}
                    alt=""
                  />
                  <p className="text-sm font-medium text-text tracking-[-0.4px]">
                    {t("pages:checkout:creditCard")}
                  </p>
                </div>
                <span
                  className="w-5 h-5 rounded-full border-2 border-primary flex items-center justify-center shrink-0"
                  aria-hidden="true"
                >
                  {selectedMethod === "card" && (
                    <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </span>
              </button>

              <div className="w-full h-px bg-border" />

              <button
                type="button"
                onClick={() => setSelectedMethod("pix")}
                className="flex items-center justify-between w-full text-start"
              >
                <div className="flex items-center gap-3">
                  <img
                    src="/svg/icon-pix-2.0.svg"
                    width={24}
                    height={24}
                    alt=""
                  />
                  <p className="text-sm font-medium text-text tracking-[-0.4px]">
                    {t("pages:checkout:pix")}
                  </p>
                </div>
                <span
                  className="w-5 h-5 rounded-full border-2 border-primary flex items-center justify-center shrink-0"
                  aria-hidden="true"
                >
                  {selectedMethod === "pix" && (
                    <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                  )}
                </span>
              </button>

              {savedCard ? (
                <>
                  <div className="w-full h-px bg-border" />

                  <button
                    type="button"
                    onClick={() => setSelectedMethod("savedCard")}
                    className="flex items-center justify-between w-full text-start"
                  >
                    <div className="flex items-center gap-3">
                      {hasBrandIcon ? (
                        <img
                          src={`/icon-credit-card-${brandName}.png`}
                          width={32}
                          height={32}
                          alt={brandName}
                          className="rounded object-contain"
                        />
                      ) : (
                        <img
                          src="/svg/icon-card-2.0.svg"
                          width={24}
                          height={24}
                          alt=""
                        />
                      )}
                      <p className="text-sm font-medium text-text tracking-[-0.4px]">
                        {brandName
                          ? `${brandName.charAt(0).toUpperCase() + brandName.slice(1)}  **** ${lastFour}`
                          : `**** ${lastFour}`}
                      </p>
                    </div>
                    <span
                      className="w-5 h-5 rounded-full border-2 border-primary flex items-center justify-center shrink-0"
                      aria-hidden="true"
                    >
                      {selectedMethod === "savedCard" && (
                        <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                      )}
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <div className="w-full h-px bg-border" />

                  <button
                    type="button"
                    onClick={goToNewCard}
                    className="flex items-center justify-center gap-3 w-full h-[50px] bg-background border border-dashed border-border rounded-2xl"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="text-text-secondary"
                      />
                      <path
                        d="M12 8v8M8 12h8"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        className="text-text-secondary"
                      />
                    </svg>
                    <p className="text-sm font-medium text-text tracking-[-0.4px]">
                      Novo cartão
                    </p>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <OrgPayment
        id="checkout-confirm-payment"
        data-testid="checkout-confirm-payment"
        textLabel={savedCard ? "Confirmar" : t("pages:checkout:confirmPayment")}
        onClick={handleConfirm}
        disabled={!selectedMethod}
      />
    </div>
  );
}
